import React, { useState, useEffect, lazy, Suspense } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ServiceDescriptionDialog } from "@/components/ServiceDescriptionDialog";
import { 
  Computer, 
  Palette, 
  PenTool, 
  FileText, 
  GraduationCap,
  Search,
  Filter,
  ArrowLeft,
  Camera,
  Music,
  Languages,
  HeartPulse,
  Shirt,
  ShoppingBag,
  MessageCircle,
  Eye,
  Gamepad2,
  TrendingUp,
  Cog
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useServices } from "@/hooks/useServices";
import { useUserProfiles } from "@/hooks/useUserProfiles";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ServiceLocationLabel } from "@/components/ServiceLocationLabel";
const ServiceRegionOverlay = lazy(() => import("@/components/ServiceRegionOverlay").then(m => ({ default: m.ServiceRegionOverlay })));
import { getCachedLocation } from "@/utils/locationUtils";
import { UserAvatar } from "@/components/UserAvatar";
import { AvailabilityDisplay, WeeklySchedule } from "@/components/services/AvailabilitySchedule";

// Category icons map
const categoryIcons: Record<string, React.FC<{ className?: string }>> = {
  "Coding": Computer,
  "Design": PenTool,
  "Writing": FileText,
  "Marketing and Sales": TrendingUp,
  "Gaming": Gamepad2,
  "Tutoring": GraduationCap,
  "Photography": Camera,
  "Music": Music,
  "Language": Languages,
  "Chores": HeartPulse,
  "Fashion": Shirt,
  "Business": ShoppingBag,
  "Arts": Palette,
  "Engineering and CAD": Cog,
  "Other": Computer
};

// Category colors
const categoryColors: Record<string, string> = {
  "Coding": "bg-blue-500 text-white",
  "Design": "bg-pink-500 text-white",
  "Writing": "bg-green-500 text-white",
  "Marketing and Sales": "bg-orange-500 text-white",
  "Gaming": "bg-purple-500 text-white",
  "Tutoring": "bg-amber-500 text-white",
  "Photography": "bg-indigo-500 text-white",
  "Music": "bg-red-500 text-white",
  "Language": "bg-emerald-500 text-white",
  "Chores": "bg-rose-500 text-white",
  "Fashion": "bg-fuchsia-500 text-white",
  "Business": "bg-cyan-500 text-white",
  "Arts": "bg-violet-500 text-white",
  "Engineering and CAD": "bg-slate-500 text-white",
  "Other": "bg-gray-500 text-white"
};

const CategoryPage = () => {
  const navigate = useNavigate();
  const { categoryId } = useParams<{ categoryId: string }>();
  const { categories, services, isLoading: servicesLoading, updateFilters } = useServices();
  const { getUserProfile } = useUserProfiles();
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [priceRange, setPriceRange] = useState("");
  const [deliveryMethod, setDeliveryMethod] = useState("");
  const [filteredServices, setFilteredServices] = useState<any[]>([]);
  const [userProfiles, setUserProfiles] = useState<Record<string, any>>({});
  const [serviceConversations, setServiceConversations] = useState<any[]>([]);
  const [requestingService, setRequestingService] = useState<string | null>(null);
  const [serviceLocations, setServiceLocations] = useState<Record<string, string>>({});
  const [regionOverlay, setRegionOverlay] = useState<{
    isOpen: boolean;
    serviceLocation?: { latitude: number; longitude: number; radius: number };
    serviceTitle?: string;
  }>({ isOpen: false });
  const [descriptionDialog, setDescriptionDialog] = useState<{
    isOpen: boolean;
    title: string;
    description: string;
  }>({ isOpen: false, title: "", description: "" });

  // Get category by ID
  const category = categories?.find(cat => cat.id === categoryId);
  const categoryName = category ? category.name : "Uncategorized";

  // Update filters in the useServices hook
  useEffect(() => {
    updateFilters({
      searchQuery,
      categoryId,
      priceRange,
      deliveryMethod: deliveryMethod as any
    });
  }, [searchQuery, categoryId, priceRange, deliveryMethod, updateFilters]);

  // Filter services based on current category and other filters
  useEffect(() => {
    if (!services) return;
    
    let filtered = [...services];
    
    // Filter by category (this is the main filter for this page)
    if (categoryId) {
      filtered = filtered.filter(service => service.category_id === categoryId);
    }
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(service => 
        service.title.toLowerCase().includes(query) || 
        (service.description && service.description.toLowerCase().includes(query))
      );
    }
    
    // Filter by price range
    if (priceRange && priceRange !== "any") {
      if (priceRange === "100+") {
        filtered = filtered.filter(service => service.price >= 100);
      } else {
        const [min, max] = priceRange.split("-").map(Number);
        filtered = filtered.filter(service => service.price >= min && service.price <= max);
      }
    }
    
    // Filter by delivery method
    if (deliveryMethod && deliveryMethod !== "any") {
      filtered = filtered.filter(service => {
        if (deliveryMethod === "both") return true;
        return service.delivery_method === deliveryMethod || service.delivery_method === "both";
      });
    }
    
    setFilteredServices(filtered);
  }, [services, categoryId, searchQuery, priceRange, deliveryMethod]);

  // Load user profiles for service creators
  useEffect(() => {
    const loadUserProfiles = async () => {
      if (filteredServices && filteredServices.length > 0) {
        const userIds = [...new Set(filteredServices.map(service => service.user_id))];
        const profiles: Record<string, any> = {};
        
        for (const userId of userIds) {
          try {
            const profile = await getUserProfile(userId);
            profiles[userId] = profile;
          } catch (error) {
            console.error(`Error loading profile for ${userId}:`, error);
          }
        }
        
        setUserProfiles(profiles);
      }
    };
    
    loadUserProfiles();
  }, [filteredServices, getUserProfile]);

  // Load existing conversations related to services
  useEffect(() => {
    const loadServiceConversations = async () => {
      if (!user) return;
      
      try {
        const { data, error } = await supabase
          .from('conversations')
          .select('id, service_id')
          .not('service_id', 'is', null)
          .eq('service_completed', false)
          .or(`participant1_id.eq.${user.id},participant2_id.eq.${user.id}`);
        
        if (error) {
          console.error('Error loading service conversations:', error);
          return;
        }
        
        if (data) {
          setServiceConversations(data);
        }
      } catch (error) {
        console.error('Error fetching service conversations:', error);
      }
    };
    
    loadServiceConversations();
  }, [user]);

  // Load location names for services
  useEffect(() => {
    const loadServiceLocations = async () => {
      if (!filteredServices) return;
      
      const locations: Record<string, string> = {};
      
      for (const service of filteredServices) {
        if (service.service_location && service.delivery_method !== "virtual") {
          try {
            const locationName = await getCachedLocation(
              service.service_location.latitude,
              service.service_location.longitude
            );
            locations[service.id] = locationName;
          } catch (error) {
            console.error(`Error loading location for service ${service.id}:`, error);
          }
        }
      }
      
      setServiceLocations(locations);
    };
    
    loadServiceLocations();
  }, [filteredServices]);

  // Check if a conversation already exists for a service
  const getConversationForService = (serviceId: string): string | null => {
    const conversation = serviceConversations.find(sc => sc.service_id === serviceId);
    return conversation ? conversation.id : null;
  };

  // Handle requesting a service
  const handleRequestService = async (service: any) => {
    if (!user) {
      toast.info("Please sign in to request this service");
      navigate("/auth?mode=login&redirect=" + encodeURIComponent(window.location.pathname));
      return;
    }
    
    if (service.user_id === user.id) {
      toast.error("You cannot request your own service");
      return;
    }
    
    setRequestingService(service.id);
    
    try {
      const existingConvo = getConversationForService(service.id);
      
      if (existingConvo) {
        navigate(`/chat/${existingConvo}`);
        return;
      }
      
      const initialMessage = `Hi, I'm interested in your ${service.title} service.`;
      
      const { data: newConvo, error: convoError } = await supabase
        .from('conversations')
        .insert({
          participant1_id: user.id,
          participant2_id: service.user_id,
          service_id: service.id,
          last_message: initialMessage
        })
        .select('id')
        .single();
      
      if (convoError || !newConvo) {
        throw new Error("Failed to create conversation");
      }
      
      const { error: msgError } = await supabase
        .from('messages')
        .insert({
          conversation_id: newConvo.id,
          sender_id: user.id,
          text: initialMessage
        });
      
      if (msgError) {
        throw new Error("Failed to send initial message");
      }
      
      setServiceConversations(prev => [
        ...prev, 
        { id: newConvo.id, service_id: service.id }
      ]);
      
      toast.success("Service requested! Opening conversation...");
      navigate(`/chat/${newConvo.id}`);
      
    } catch (error) {
      console.error("Error requesting service:", error);
      toast.error("Failed to request service. Please try again.");
    } finally {
      setRequestingService(null);
    }
  };

  // Reset all filters
  const handleResetFilters = () => {
    setSearchQuery("");
    setPriceRange("");
    setDeliveryMethod("");
  };

  // Handle viewing service region
  const handleViewServiceRegion = (service: any) => {
    if (!service.service_location) return;
    
    setRegionOverlay({
      isOpen: true,
      serviceLocation: service.service_location,
      serviceTitle: service.title
    });
  };

  // Handle closing region overlay
  const handleCloseRegionOverlay = () => {
    setRegionOverlay({ isOpen: false });
  };

  // Category images and descriptions
  const categoryImages: Record<string, string> = {
    "Web Development": "https://images.unsplash.com/photo-1461749280684-dccba630e2f6?auto=format&fit=crop&w=1200&q=80",
    "Design": "https://images.unsplash.com/photo-1586717799252-bd134ad00e26?auto=format&fit=crop&w=1200&q=80",
    "Writing": "https://images.unsplash.com/photo-1455390582262-044cdead277a?auto=format&fit=crop&w=1200&q=80",
    "Marketing and Sales": "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1200&q=80",
    "Game Development": "https://images.unsplash.com/photo-1511512578047-dfb367046420?auto=format&fit=crop&w=1200&q=80",
    "Tutoring": "https://images.unsplash.com/photo-1427504494785-3a9ca7044f45?auto=format&fit=crop&w=1200&q=80",
    "Photography": "https://images.unsplash.com/photo-1606983340126-99ab4feaa64a?auto=format&fit=crop&w=1200&q=80",
    "Music": "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?auto=format&fit=crop&w=1200&q=80",
    "Language": "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?auto=format&fit=crop&w=1200&q=80",
    "Health": "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?auto=format&fit=crop&w=1200&q=80",
    "Fashion": "https://images.unsplash.com/photo-1445205170230-053b83016050?auto=format&fit=crop&w=1200&q=80",
    "Business": "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=1200&q=80",
    "Arts": "https://images.unsplash.com/photo-1460661419201-fd4cecdf8a8b?auto=format&fit=crop&w=1200&q=80",
    "Engineering and CAD": "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&w=1200&q=80",
    "Other": "https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?auto=format&fit=crop&w=1200&q=80"
  };

  const categoryDescriptions: Record<string, string> = {
    "Web Development": "Find talented developers to create websites, web applications, and digital solutions tailored to your needs.",
    "Design": "Connect with creative designers for logos, graphics, UI/UX design, and visual branding that makes your project stand out.",
    "Writing": "Discover skilled writers for content creation, copywriting, technical documentation, and creative writing projects.",
    "Marketing and Sales": "Get help with marketing strategies, social media management, advertising campaigns, and sales optimization.",
    "Game Development": "Find game developers and designers to bring your gaming ideas to life with professional development skills.",
    "Tutoring": "Connect with experienced tutors and educators for personalized learning in various subjects and skills.",
    "Photography": "Hire professional photographers for events, portraits, product photography, and creative visual content.",
    "Music": "Find musicians, composers, and audio professionals for music production, sound design, and audio services.",
    "Language": "Connect with language experts for translation, interpretation, language learning, and linguistic services.",
    "Health": "Access health and wellness professionals for fitness training, nutrition advice, and wellness coaching.",
    "Fashion": "Find fashion designers, stylists, and consultants for clothing design, personal styling, and fashion advice.",
    "Business": "Get business consulting, strategy development, project management, and professional business services.",
    "Arts": "Connect with artists for custom artwork, illustrations, crafts, and creative artistic projects.",
    "Engineering and CAD": "Find engineering professionals and CAD specialists for technical design and engineering solutions.",
    "Other": "Explore various other professional services and specialized skills for your unique project needs."
  };

  const categoryImage = categoryImages[categoryName] || categoryImages["Other"];
  const categoryDescription = categoryDescriptions[categoryName] || categoryDescriptions["Other"];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="container mx-auto px-4 py-4 flex-grow">
        <div className="flex items-center mb-3">
          <Link to="/discover-services" className="flex items-center text-muted-foreground hover:text-anyskill-purple mr-4">
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Services
          </Link>
          <h1 className="font-poppins font-bold text-xl md:text-2xl">
            Services in <span className="text-anyskill-blue">{categoryName}</span>
          </h1>
        </div>

        {/* Compact Search and Filters */}
        <div className="flex flex-col gap-2 mb-4">
          <div className="flex flex-col md:flex-row gap-2">
            <div className="relative flex-grow">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input 
                placeholder="Search services..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-8"
              />
            </div>
            
            <div className="flex gap-2 flex-wrap">
              <Select value={priceRange} onValueChange={setPriceRange}>
                <SelectTrigger className="w-[100px] h-8">
                  <SelectValue placeholder="Price" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any Price</SelectItem>
                  <SelectItem value="0-25">$0 - $25</SelectItem>
                  <SelectItem value="25-50">$25 - $50</SelectItem>
                  <SelectItem value="50-100">$50 - $100</SelectItem>
                  <SelectItem value="100+">$100+</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={deliveryMethod} onValueChange={setDeliveryMethod}>
                <SelectTrigger className="w-[100px] h-8">
                  <SelectValue placeholder="Method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="virtual">Virtual</SelectItem>
                  <SelectItem value="in-person">In-Person</SelectItem>
                  <SelectItem value="both">Both</SelectItem>
                </SelectContent>
              </Select>

              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-8">
                    <Filter className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64">
                  <div className="grid gap-3">
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm">Location</h4>
                      <Select>
                        <SelectTrigger className="h-8">
                          <SelectValue placeholder="Select location" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="local">Local Only</SelectItem>
                          <SelectItem value="remote">Remote Only</SelectItem>
                          <SelectItem value="any-location">Any Location</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm">Availability</h4>
                      <Select>
                        <SelectTrigger className="h-8">
                          <SelectValue placeholder="Select availability" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="available">Available Now</SelectItem>
                          <SelectItem value="weekdays">Weekdays</SelectItem>
                          <SelectItem value="weekends">Weekends</SelectItem>
                          <SelectItem value="any-time">Any Time</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
              
              {(searchQuery || priceRange || deliveryMethod) && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="text-muted-foreground h-8"
                  onClick={handleResetFilters}
                >
                  Clear
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Category Hero Section */}
        <div className="mb-8">
          <div className="relative h-48 md:h-64 rounded-lg overflow-hidden">
            <img 
              src={categoryImage} 
              alt={categoryName}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
              <div className="text-center text-white px-4">
                <h2 className="text-2xl md:text-4xl font-bold mb-2">{categoryName}</h2>
                <p className="text-lg md:text-xl opacity-90 max-w-2xl">
                  {categoryDescription}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Services Listings */}
        <div>
          <h2 className="font-poppins font-semibold text-xl mb-4">
            Available Services
          </h2>
          
          {servicesLoading ? (
            <div className="flex justify-center my-8">
              <p>Loading services...</p>
            </div>
          ) : filteredServices.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredServices.map((service) => {
                const userProfile = userProfiles[service.user_id] || { 
                  name: "Unknown User", 
                  avatarInitials: "UN",
                  avatarUrl: undefined 
                };
                const conversationId = getConversationForService(service.id);
                const isOwnService = user && service.user_id === user.id;
                const locationName = serviceLocations[service.id];
                const IconComponent = categoryIcons[categoryName] || Computer;
                const colorClass = categoryColors[categoryName] || "bg-gray-500 text-white";
                
                return (
                  <Card key={service.id} className="overflow-hidden hover:shadow-md transition-shadow relative border-2 border-green-200">
                    {/* Service Badge */}
                    <div className="absolute top-2 left-2 z-10">
                      <Badge variant="secondary" className="bg-green-500 text-white text-xs font-medium px-2 py-1">
                        SERVICE
                      </Badge>
                    </div>

                    <div className="aspect-[4/3] bg-gray-100 dark:bg-gray-700 relative overflow-hidden">
                      {service.image_url ? (
                        <>
                          <img 
                            src={service.image_url} 
                            alt={service.title}
                            className="w-full h-full object-contain bg-gray-50 dark:bg-gray-800"
                          />
                          <div className="absolute top-2 right-2">
                            <div className={`${colorClass} p-1.5 rounded-full shadow-lg border-2 border-white backdrop-blur-sm bg-opacity-90`}>
                              <IconComponent className="h-4 w-4" />
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <div className={`${colorClass} p-2 rounded-full`}>
                            <IconComponent className="h-6 w-6" />
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <CardContent className="p-4">
                      <Link to={`/profile/${service.user_id}`} className="flex items-center mb-3 hover:text-anyskill-blue">
                        <UserAvatar
                          userId={service.user_id}
                          avatarUrl={userProfile.avatarUrl}
                          fallback={userProfile.avatarInitials}
                          size="sm"
                          className="mr-2"
                        />
                        <span className="text-sm text-muted-foreground hover:text-anyskill-blue truncate">{userProfile.name}</span>
                      </Link>
                      <CardTitle className="text-base mb-3 line-clamp-2 leading-tight">{service.title}</CardTitle>
                      <p 
                        className="text-sm text-muted-foreground mb-3 line-clamp-3 cursor-pointer hover:text-foreground transition-colors"
                        onClick={() => setDescriptionDialog({
                          isOpen: true,
                          title: service.title,
                          description: service.description || "No description provided."
                        })}
                        title="Click to read full description"
                      >
                        {service.description || "No description provided."}
                      </p>
                      
                      <ServiceLocationLabel
                        deliveryMethod={service.delivery_method}
                        location={locationName}
                        className="mb-2 text-xs"
                      />
                      
                      {service.availability_schedule && service.delivery_method !== "virtual" && (
                        <div className="mb-3">
                          <AvailabilityDisplay schedule={service.availability_schedule as WeeklySchedule} />
                        </div>
                      )}
                      
                      <div className="flex justify-between items-end">
                        <div className="flex flex-col">
                          <div className="flex items-center">
                            <span className="font-semibold text-base">${service.price}</span>
                          </div>
                        </div>
                        
                        <div className="flex gap-2 flex-wrap">
                          {service.service_location && service.delivery_method !== "virtual" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleViewServiceRegion(service)}
                              className="flex items-center gap-1 text-xs px-3 py-2 h-8"
                            >
                              <Eye size={14} />
                              <span className="hidden sm:inline">View</span>
                            </Button>
                          )}
                          
                          {!isOwnService && (
                            user ? (
                              conversationId ? (
                                <Link to={`/chat/${conversationId}`}>
                                  <Button 
                                    size="sm" 
                                    variant="outline" 
                                    className="flex items-center gap-1 text-xs px-3 py-2 h-8"
                                  >
                                    <MessageCircle size={14} />
                                    Chat
                                  </Button>
                                </Link>
                              ) : (
                                <Button 
                                  size="sm" 
                                  onClick={() => handleRequestService(service)}
                                  disabled={requestingService === service.id}
                                  className="text-xs px-3 py-2 h-8"
                                >
                                  {requestingService === service.id ? "Requesting..." : "Request"}
                                </Button>
                              )
                            ) : (
                              <Button 
                                size="sm" 
                                onClick={() => handleRequestService(service)}
                                className="text-xs px-3 py-2 h-8"
                              >
                                Request
                              </Button>
                            )
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 bg-muted rounded-lg border border-border">
              <div className="text-5xl mb-4">🔍</div>
              <h3 className="font-semibold text-xl mb-2">No services found</h3>
              <p className="text-muted-foreground">
                No services are available in this category matching your criteria.
              </p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={handleResetFilters}
              >
                Reset Filters
              </Button>
            </div>
          )}
        </div>
      </div>
      
      {/* Service Region Overlay */}
      {regionOverlay.serviceLocation && (
        <Suspense fallback={null}>
          <ServiceRegionOverlay
            isOpen={regionOverlay.isOpen}
            onClose={handleCloseRegionOverlay}
            serviceLocation={regionOverlay.serviceLocation}
            serviceTitle={regionOverlay.serviceTitle || "Service"}
          />
        </Suspense>
      )}
      
      <ServiceDescriptionDialog
        isOpen={descriptionDialog.isOpen}
        onClose={() => setDescriptionDialog({ isOpen: false, title: "", description: "" })}
        title={descriptionDialog.title}
        description={descriptionDialog.description}
      />
    </div>
  );
};

export default CategoryPage;
